import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  AccountBalanceWallet as WalletIcon,
  AutoAwesome as AssistantIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase/config';

/**
 * Einstellungen → „Zahlung per App (PayPal)" — White-Label-Selbsteinrichtung
 * (docs/paypal_app_zahlungen_konzept.md, Kapitel 8).
 *
 * Der Unternehmer verbindet sein eigenes PayPal-Konto (Client-ID + Secret) und
 * aktiviert App-Zahlungen. Die Aktivierung erzwingt serverseitig (Callable
 * `configurePayPalPayments`) atomar: showFareToCustomer = AN,
 * requireDriverFareInput = AN. Die zugehörigen Schalter werden in der
 * SettingsPage ausgegraut, solange App-Zahlungen aktiv sind
 * (`appPaymentsEnabled` wird hochgereicht).
 */
const AppPaymentSettingsSection = ({ onEnabledChange, canManage = true }) => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { severity, message }

  // Verfügbarkeit je Fahrttyp (greift nur bei aktiver App-Zahlung)
  const [fixedPriceEnabled, setFixedPriceEnabled] = useState(true);
  const [meteredEnabled, setMeteredEnabled] = useState(false);
  const [savingRideType, setSavingRideType] = useState(null); // 'fixedPrice' | 'metered' | null

  // Verbindungsstatus der eigenen PayPal-REST-App (Self-Service)
  const [paypalConnected, setPaypalConnected] = useState(false);
  const [clientIdMasked, setClientIdMasked] = useState('');
  const [connectedEnvironment, setConnectedEnvironment] = useState('sandbox');
  const [accountEmail, setAccountEmail] = useState(''); // Anzeige: wohin das Geld geht

  // Verbinden-Formular
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [connectClientId, setConnectClientId] = useState('');
  const [connectClientSecret, setConnectClientSecret] = useState('');
  const [connectAccountEmail, setConnectAccountEmail] = useState('');
  const [connecting, setConnecting] = useState(false);

  // appSettings/payments live beobachten (öffentlich lesbar, keine Secrets —
  // hier liegt nur der maskierte Status-Spiegel)
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'appSettings', 'payments'), (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : {};
      const isEnabled = data.appPaymentsEnabled === true;
      setEnabled(isEnabled);
      setPaypalConnected(data.paypalConnected === true);
      setClientIdMasked(data.paypalClientIdMasked || '');
      setConnectedEnvironment(data.environment === 'live' ? 'live' : 'sandbox');
      setAccountEmail(data.paypalAccountEmail || '');
      // Default wie in der App (PaymentClientSettings): Festpreis an, Taxameter aus
      setFixedPriceEnabled(data.paypalFixedPriceRidesEnabled !== false);
      setMeteredEnabled(data.paypalMeteredRidesEnabled === true);
      setLoading(false);
      if (onEnabledChange) onEnabledChange(isEnabled);
    }, () => setLoading(false));
    return unsubscribe;
  }, [onEnabledChange]);

  const handleConnect = async () => {
    setConnecting(true);
    setFeedback(null);
    try {
      const callable = httpsCallable(functions, 'connectPayPalCredentials');
      const result = await callable({
        clientId: connectClientId.trim(),
        clientSecret: connectClientSecret.trim(),
        accountEmail: connectAccountEmail.trim()
      });
      const data = result.data || {};
      setShowConnectForm(false);
      setConnectClientId('');
      setConnectClientSecret('');
      setConnectAccountEmail('');
      setFeedback({
        severity: 'success',
        message:
          `PayPal-Konto verbunden (${data.clientIdMasked}, ${data.environment === 'live' ? 'Live' : 'Sandbox'} automatisch erkannt). ` +
          (data.webhookRegistered
            ? 'Der Webhook wurde automatisch registriert.'
            : 'Hinweis: Der Webhook konnte nicht automatisch registriert werden — Zahlungen funktionieren, bitte Support informieren.')
      });
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: error?.message || 'Verbindung fehlgeschlagen. Bitte Zugangsdaten prüfen.'
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleToggle = async () => {
    setSaving(true);
    setFeedback(null);
    const nextEnabled = !enabled;
    try {
      const callable = httpsCallable(functions, 'configurePayPalPayments');
      await callable({ enabled: nextEnabled });
      setFeedback({
        severity: 'success',
        message: nextEnabled
          ? 'App-Zahlungen aktiviert. Fahrpreis-Anzeige und Preiseingabe wurden automatisch festgelegt.'
          : 'App-Zahlungen deaktiviert. Die gekoppelten Einstellungen sind wieder frei wählbar.'
      });
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: error?.message || 'Einstellung konnte nicht gespeichert werden.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRideTypeToggle = async (key) => {
    setSavingRideType(key);
    setFeedback(null);
    const payload = key === 'metered'
      ? { meteredEnabled: !meteredEnabled }
      : { fixedPriceEnabled: !fixedPriceEnabled };
    try {
      const callable = httpsCallable(functions, 'setPayPalRideTypes');
      await callable(payload);
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: error?.message || 'Einstellung konnte nicht gespeichert werden.'
      });
    } finally {
      setSavingRideType(null);
    }
  };

  return (
    <>
      <Box sx={{ p: 2, bgcolor: 'surface.100' }}>
        <Typography variant="subtitle1" fontWeight="bold">
          <WalletIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Zahlung per App (PayPal)
          {enabled && <Chip label="Aktiv" color="success" size="small" sx={{ ml: 1 }} />}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Kunden bezahlen Fahrten per PayPal in der App. Der Betrag wird vor der
          Fahrt reserviert und nach Fahrtende mit dem tatsächlichen Preis eingezogen.
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {!canManage && (
            <Alert severity="info" sx={{ mx: 3, my: 2 }}>
              App-Zahlungen werden von der Hauptzentrale verwaltet. Bei Fragen
              zu PayPal-Empfang oder Aktivierung wenden Sie sich bitte an Ihre
              Zentrale.
            </Alert>
          )}
          <List disablePadding>
            <ListItem
              secondaryAction={
                <Switch
                  edge="end"
                  checked={enabled}
                  onChange={handleToggle}
                  disabled={!canManage || saving || (!enabled && !paypalConnected)}
                  color="primary"
                />
              }
            >
              <ListItemIcon>
                <WalletIcon />
              </ListItemIcon>
              <ListItemText
                primary="Zahlung per App aktivieren"
                secondary={
                  enabled
                    ? 'Kunden können bei der Bestellung PayPal wählen'
                    : paypalConnected
                      ? 'Zahlungen gehen an Ihr verbundenes PayPal-Geschäftskonto'
                      : 'Zuerst unten das eigene PayPal-Konto verbinden'
                }
              />
            </ListItem>
          </List>

          {enabled && (
            <Box sx={{ px: 3, pt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Für welche Fahrten gilt die App-Zahlung?
              </Typography>
              <List disablePadding>
                <ListItem
                  secondaryAction={
                    <Switch
                      edge="end"
                      checked={fixedPriceEnabled}
                      onChange={() => handleRideTypeToggle('fixedPrice')}
                      disabled={!canManage || savingRideType !== null}
                      color="primary"
                    />
                  }
                >
                  <ListItemText
                    primary="Festpreisfahrten"
                    secondary="Der ausgewählte Festpreis wird reserviert und nach der Fahrt eingezogen. Setzt Angebote in der „Festpreisliste“ voraus."
                  />
                </ListItem>
                <ListItem
                  secondaryAction={
                    <Switch
                      edge="end"
                      checked={meteredEnabled}
                      onChange={() => handleRideTypeToggle('metered')}
                      disabled={!canManage || savingRideType !== null}
                      color="primary"
                    />
                  }
                >
                  <ListItemText
                    primary="Taxameterfahrten"
                    secondary="Vor der Fahrt wird ein Höchstbetrag (Schätzung + Reserve) reserviert; eingezogen wird der tatsächliche Endpreis."
                  />
                </ListItem>
              </List>
              {!fixedPriceEnabled && !meteredEnabled && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Kein Fahrttyp aktiv — Kunden bekommen derzeit keine PayPal-Option
                  angeboten, obwohl App-Zahlung eingeschaltet ist.
                </Alert>
              )}
            </Box>
          )}

          <Box sx={{ px: 3, py: 2, bgcolor: 'surface.50' }}>
            {/* Verbundenes Konto: Status + Empfänger-Anzeige */}
            {paypalConnected && !showConnectForm ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }} sx={{ mb: 1 }}>
                <Alert
                  severity="success"
                  sx={{ flex: 1 }}
                  action={
                    <Chip
                      size="small"
                      color={connectedEnvironment === 'live' ? 'success' : 'warning'}
                      label={connectedEnvironment === 'live' ? 'Live' : 'Sandbox (Test)'}
                    />
                  }
                >
                  PayPal-Konto verbunden: <strong>{clientIdMasked}</strong>
                  <br />
                  Zahlungen gehen an:{' '}
                  <strong>{accountEmail || 'Ihr verbundenes PayPal-Geschäftskonto'}</strong>
                </Alert>
                {canManage && (
                  <Button size="small" variant="outlined" onClick={() => setShowConnectForm(true)}>
                    Zugangsdaten ändern
                  </Button>
                )}
              </Stack>
            ) : (
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  {paypalConnected ? 'Zugangsdaten ändern' : 'Eigenes PayPal-Konto verbinden'}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                  Melden Sie sich mit Ihrem PayPal-<strong>Geschäftskonto</strong> auf{' '}
                  <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noreferrer">
                    developer.paypal.com
                  </a>{' '}
                  an, legen Sie unter „Apps &amp; Credentials" eine App an und
                  kopieren Sie Client-ID und Secret hierher. Die Daten werden
                  geprüft, sicher gespeichert und die PayPal-Benachrichtigungen
                  automatisch eingerichtet.
                </Typography>
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Client-ID"
                    value={connectClientId}
                    onChange={(event) => setConnectClientId(event.target.value)}
                    disabled={!canManage || connecting}
                    autoComplete="off"
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    label="Secret"
                    value={connectClientSecret}
                    onChange={(event) => setConnectClientSecret(event.target.value)}
                    disabled={!canManage || connecting}
                    autoComplete="new-password"
                  />
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    label="PayPal-E-Mail-Adresse (optional, nur zur Anzeige)"
                    placeholder="ihr-konto@beispiel.de"
                    value={connectAccountEmail}
                    onChange={(event) => setConnectAccountEmail(event.target.value)}
                    helperText="Wird nur angezeigt, damit sichtbar ist, wohin das Geld fließt. Der Empfänger ergibt sich aus den Zugangsdaten."
                    disabled={!canManage || connecting}
                  />
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                      component={RouterLink}
                      to="/einstellungen/paypal-einrichtung"
                      variant="outlined"
                      startIcon={<AssistantIcon />}
                      disabled={!canManage || connecting}
                    >
                      Assistent
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    {paypalConnected && (
                      <Button size="small" variant="outlined" onClick={() => setShowConnectForm(false)} disabled={connecting}>
                        Abbrechen
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={handleConnect}
                      disabled={!canManage || connecting || !connectClientId.trim() || !connectClientSecret.trim()}
                    >
                      {connecting ? 'Wird geprüft…' : 'Verbinden & prüfen'}
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    Die Umgebung (Live oder Sandbox) wird automatisch anhand Ihrer Zugangsdaten erkannt.
                  </Typography>
                </Stack>
              </Box>
            )}

            {feedback && (
              <Alert severity={feedback.severity} sx={{ mt: 2 }} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Bei aktiver App-Zahlung werden diese Einstellungen automatisch
                festgelegt und gesperrt:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                <li>„Fahrpreis-Schätzung für Kunden anzeigen" — <strong>an</strong></li>
                <li>„Fahrpreis nach App-Fahrt erfassen" — <strong>an</strong></li>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                PayPal-Gebühren und Auszahlungen verwalten Sie direkt in Ihrem
                PayPal-Geschäftskonto.
              </Typography>
            </Alert>
          </Box>
        </>
      )}
    </>
  );
};

export default AppPaymentSettingsSection;
